// src/pages/ChefProjetDetailPage.tsx — SCE Issues
// Fiche d'un chef de projet : ses PN, et ce qui s'y passe.
//
// ─── Pourquoi cette fiche existe ───
//
// Le chef de projet n'était jusqu'ici qu'une chaîne affichée en bas d'une
// fiche équipement, et un filtre du dashboard. Or c'est une entrée d'enquête
// naturelle : « sur quels projets Sébastien est-il, et lesquels posent
// problème ? » demandait d'ouvrir le dashboard, de poser un filtre, puis de
// revenir.
//
// ⚠️ Le chef de projet est un NOM LIBRE venu de Windchill, pas un compte
// Azure AD. Il n'y a donc ni identifiant stable ni garantie d'unicité : deux
// homonymes seraient confondus. Le rapprochement se fait sur le libellé exact
// (à la casse et aux espaces près), comme partout ailleurs dans l'application
// — le dashboard filtre déjà ainsi.
//
// ─── Aucune requête dédiée ───
//
// Tout vient du cache mémoire de equipements.api.ts et du useIssues() global,
// déjà chargés ailleurs dans l'application.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CadreFiche } from '@/components/layout/CadreFiche'
import { useOuvrirFiche } from '@/components/layout/ModaleRoute'
import {
  EtatVide,
  LigneCompacte,
  TitreSection,
} from '@/components/issues/LigneCompacte'
import { useIssues } from '@/hooks/useIssues'
import { listAllEquipements } from '@/api/equipements.api'
import { lienProjet } from '@/utils/liensFiches'
import { nomComplet } from '@/utils/noms'
import {
  cleRapprochement,
  compteCommeProbleme,
  type Equipement,
} from '@/types/issue.types'

interface Projet {
  pn: string
  equipements: Equipement[]
  cve?: string
}

export function ChefProjetDetailPage(): JSX.Element {
  const navigate = useNavigate()
  const ouvrirFiche = useOuvrirFiche()
  // Un ancien lien porte l'identifiant brut (« lucile.goy1 ») : même format
  // que la liste, « Lucile Goy ».
  const { nom: nomBrut } = useParams<{ nom: string }>()
  const nom = nomBrut ? nomComplet(nomBrut) : nomBrut

  const [tous, setTous] = useState<Equipement[]>([])
  const [loading, setLoading] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)

  // Sans filtre : le décompte par projet se fait côté client, sur le même
  // chargement que l'accueil et le dashboard.
  const { issues } = useIssues()

  useEffect(() => {
    let annule = false
    setLoading(true)
    setErreur(null)
    listAllEquipements()
      .then((eqs) => {
        if (!annule) setTous(eqs)
      })
      .catch((err: unknown) => {
        if (!annule) {
          setErreur(err instanceof Error ? err.message : 'Erreur de chargement')
        }
      })
      .finally(() => {
        if (!annule) setLoading(false)
      })
    return () => {
      annule = true
    }
  }, [])

  const projets = useMemo<Projet[]>(() => {
    const cible = (nom ?? '').trim().toLowerCase()
    if (!cible) return []

    const siens = tous.filter(
      (e) => (e.chefProjet ?? '').trim().toLowerCase() === cible,
    )

    const parPn = new Map<string, Equipement[]>()
    for (const eq of siens) {
      const cle = eq.pn.trim() || '(sans PN)'
      const liste = parPn.get(cle)
      if (liste) liste.push(eq)
      else parPn.set(cle, [eq])
    }

    return [...parPn.entries()]
      .map(([pn, equipements]) => {
        const projet: Projet = { pn, equipements }
        const cve = equipements.find((e) => e.cve)?.cve
        if (cve) projet.cve = cve
        return projet
      })
      .sort((a, b) => a.pn.localeCompare(b.pn, 'fr'))
  }, [tous, nom])

  const nbEquipements = projets.reduce((n, p) => n + p.equipements.length, 0)

  // Nombre de problèmes par PN — un simple décompte, pas un filtre serveur.
  //
  // ⚠️ Clé normalisée en minuscules, comme partout où l'on rapproche un PN :
  // Windchill ne normalise pas la casse, et un décompte indexé sur la graphie
  // brute affichait zéro sur les projets dont la déclaration portait une autre
  // casse que le référentiel.
  const problemesParPn = useMemo(() => {
    const m = new Map<string, number>()
    for (const i of issues) {
      // Un annulé ne fait pas passer un projet pour « à problèmes ».
      if (!compteCommeProbleme(i)) continue
      const cle = cleRapprochement(i.pn)
      if (cle) m.set(cle, (m.get(cle) ?? 0) + 1)
    }
    return m
  }, [issues])

  const titre = nom ?? 'Chef de projet'

  if (loading) {
    return (
      <CadreFiche titre={titre} onBack={() => navigate('/equipements')}>
        <div className="p-4 flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="rounded-lg animate-pulse"
              style={{ height: 56, backgroundColor: 'var(--color-gray-10)' }}
            />
          ))}
        </div>
      </CadreFiche>
    )
  }

  if (erreur) {
    return (
      <CadreFiche titre={titre} onBack={() => navigate('/equipements')}>
        <div className="p-4">
          <EtatVide>{erreur}</EtatVide>
        </div>
      </CadreFiche>
    )
  }

  if (projets.length === 0) {
    return (
      <CadreFiche titre={titre} onBack={() => navigate('/equipements')}>
        <div className="p-4">
          <EtatVide>Aucun projet rattaché à ce chef de projet.</EtatVide>
        </div>
      </CadreFiche>
    )
  }

  return (
    <CadreFiche titre={titre} onBack={() => navigate('/equipements')}>
      <div className="p-4 flex flex-col gap-5">
        {/* Résumé */}
        <section
          className="rounded-lg p-4 flex"
          style={{
            backgroundColor: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
          }}
        >
          <Compteur valeur={projets.length} label={projets.length > 1 ? 'Projets' : 'Projet'} />
          <Compteur
            valeur={nbEquipements}
            label={nbEquipements > 1 ? 'Équipements' : 'Équipement'}
          />
        </section>

        {/* Ses projets */}
        <section>
          <TitreSection compteur={projets.length}>Projets</TitreSection>
          <div className="flex flex-col gap-2">
            {projets.map((p) => {
              const nbPb = problemesParPn.get(cleRapprochement(p.pn)) ?? 0
              return (
                <LigneCompacte
                  key={p.pn}
                  titre={p.pn}
                  sousTitre={
                    [
                      `${p.equipements.length} équipement${p.equipements.length > 1 ? 's' : ''}`,
                      p.cve,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  }
                  droite={nbPb > 0 ? `${nbPb} pb` : undefined}
                  couleurDroite={nbPb > 1 ? 'var(--color-traffic-red)' : undefined}
                  onClick={() => ouvrirFiche(lienProjet(p.pn))}
                />
              )
            })}
          </div>
        </section>
      </div>
    </CadreFiche>
  )
}

// ─── Sous-composants ───

function Compteur({ valeur, label }: { valeur: number; label: string }): JSX.Element {
  return (
    <div className="flex-1 flex flex-col items-center">
      <span
        style={{
          fontSize: 'var(--font-size-h2)',
          lineHeight: 'var(--line-height-h2)',
          fontWeight: 500,
        }}
      >
        {valeur}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-info)',
          color: 'var(--color-text-secondary)',
        }}
      >
        {label}
      </span>
    </div>
  )
}
